import { useEffect, useReducer, useRef } from 'react';
import type { OnlineStatus } from '../game/session';
import { connectionPane } from './connectionPane';

interface Props {
  status: OnlineStatus;
  /** The rules have ended the game: there is no seat left to wait for. */
  finished: boolean;
  /** OK on the reclaim pane: the person takes their seat back. */
  onReclaim: () => void;
}

/**
 * The panes of a dropped connection (D-29) over the table and the arena: the countdown before
 * the AI stands in for someone, this player's own lost connection, and the one button that takes
 * their seat back once they are ready. They hold the screen until they are done, like any modal.
 */
export function ConnectionPaneOverlay({ status, finished, onReclaim }: Props) {
  const [, tick] = useReducer((n: number) => n + 1, 0);
  const pane = connectionPane(status, performance.now(), finished);
  const counting = pane !== null && !pane.ok;
  useEffect(() => {
    if (!counting) return;
    const timer = setInterval(tick, 250);
    return () => clearInterval(timer);
  }, [counting]);
  const button = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (pane?.ok) button.current?.focus();
  }, [pane?.ok]);
  if (!pane) return null;
  return (
    <div
      className="card-battle hand-over connection-pane"
      role="alertdialog"
      aria-modal="true"
      aria-label={pane.title}
    >
      <section className="hand-over-panel" data-testid="connection-pane" data-kind={pane.kind}>
        <p className="hand-over-title">{pane.title}</p>
        <p className="hint" data-testid="connection-pane-text">
          {pane.text}
        </p>
        {pane.ok && (
          <button
            ref={button}
            className="primary"
            data-testid="connection-pane-ok"
            onClick={onReclaim}
          >
            OK
          </button>
        )}
      </section>
    </div>
  );
}
